import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef } from 'react';

import { AchEnrollmentStatus, useFuelAndConvenienceProfileCreateOrUpdateMutation } from '@ocx/graphql';
import { logInDev } from '@ocx/utils';

import { SPIRE_EVENT_TO_ACH_STATUS_MAP } from '../spire.constants';
import { SpirePostMessageEvent, SpireEvent } from '../spire.types';

type UseSpirePostMessageParams = {
  iframeUrl: string;
  skip?: boolean;
  onEvent?: (event: SpireEvent) => void;
  onStatusUpdateSuccess?: (status: AchEnrollmentStatus | null) => void;
  onStatusUpdateError?: (error: Error) => void;
};

const isValidSpireEvent = (event: string): event is SpireEvent => {
  return event in SPIRE_EVENT_TO_ACH_STATUS_MAP;
};

export const useSpirePostMessage = ({
  iframeUrl,
  skip,
  onEvent,
  onStatusUpdateSuccess,
  onStatusUpdateError,
}: UseSpirePostMessageParams) => {
  const lastEventRef = useRef<SpireEvent | null>(null);

  const [updateAchStatus] = useFuelAndConvenienceProfileCreateOrUpdateMutation({
    onError: (error) => {
      logInDev(`[Spire] ACH status update failed: ${error.message}`);
      Sentry.captureException(error, {
        tags: { feature: 'spire-enrollment' },
        extra: { lastEvent: lastEventRef.current },
      });
      onStatusUpdateError?.(error as Error);
    },
    onCompleted: (data) => {
      logInDev('[Spire] ACH status updated successfully');
      onStatusUpdateSuccess?.(data.fuelAndConvenienceProfile.achEnrollmentStatus);
    },
  });

  const handleAchStatusUpdate = useCallback(
    async (achStatus: AchEnrollmentStatus) => {
      try {
        await updateAchStatus({
          variables: {
            input: {
              achEnrollmentStatus: achStatus,
            },
          },
        });
      } catch (error) {
        logInDev(`[Spire] Mutation error: ${error}`);
      }
    },
    [updateAchStatus],
  );

  useEffect(() => {
    if (skip) {
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      try {
        // Validate origin matches iframe URL origin (Spire uses bimnetworks.com)
        const { origin: expectedOrigin } = new URL(iframeUrl);
        if (event.origin !== expectedOrigin) {
          logInDev(`[Spire] Ignoring message from unexpected origin: ${event.origin}`);
          return;
        }

        // Parse and validate message data
        const data = event.data as Partial<SpirePostMessageEvent>;
        if (!data.Event) {
          logInDev(`[Spire] Ignoring non-Spire message: ${JSON.stringify(data)}`);
          return;
        }

        // Validate event is a known SpireEvent value
        if (!isValidSpireEvent(data.Event)) {
          logInDev(`[Spire] Ignoring unknown event: ${data.Event}`);
          Sentry.captureMessage(`Unknown Spire event received: ${data.Event}`, {
            level: 'warning',
            tags: { feature: 'spire-enrollment' },
            extra: { eventData: data },
          });
          return;
        }

        const spireEvent = data.Event;

        // Avoid duplicate processing of same event
        if (lastEventRef.current === spireEvent) {
          logInDev(`[Spire] Skipping duplicate event: ${spireEvent}`);
          return;
        }

        lastEventRef.current = spireEvent;
        onEvent?.(spireEvent);

        // Map Spire event to ACH status and update if applicable
        const achStatus = SPIRE_EVENT_TO_ACH_STATUS_MAP[spireEvent];
        if (achStatus) {
          logInDev(`[Spire] Updating ACH status: ${spireEvent} → ${achStatus}`);
          await handleAchStatusUpdate(achStatus);
        } else {
          logInDev(`[Spire] No ACH status update needed for: ${spireEvent}`);
        }
      } catch (error) {
        logInDev(`[Spire] Error handling postMessage: ${error}`);
        Sentry.captureException(error, {
          tags: { feature: 'spire-enrollment' },
          extra: { eventData: event.data },
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [skip, iframeUrl, onEvent, handleAchStatusUpdate]);
};
